import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { get, matches } from '@sama/micro-dash';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { KeypointShapeState } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { forbiddenCursor } from '../../../../cross-team-shared/cursors/custom-cursors';
import { setCssProperty } from '../../../../cross-team-shared/misc-utils';
import {
  KeypointOptionClass,
  KeypointOptionConnection,
  KeypointOptionPoint,
} from '../../../../team-apis/otterdam/keypoint-options';
import { SubpointInfo } from '../../subpoint/subpoint.component';
import { getKeypointOptionClasses } from '../../tools/keypoint/keypoint-tool';
import { getPointString } from '../abstract-shape-helpers';
import { AbstractShapeComponent } from '../abstract-shape.component';

interface Connection {
  pointString: string;
  color?: string;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- svg needs attribute selectors
  selector: '[h3KeypointShape]',
  templateUrl: './keypoint-shape.component.html',
  styleUrls: [
    '../line-shape-styles.scss',
    '../../shape-parts/outline-host.scss',
    './keypoint-shape.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KeypointShapeComponent
  extends AbstractShapeComponent
  implements OnInit
{
  connections$!: Observable<Connection[]>;

  private optionClass?: KeypointOptionClass;

  override ngOnInit() {
    super.ngOnInit();
    const options = getKeypointOptionClasses(this.store.state().output);
    this.optionClass = options.find(
      matches({ name: this.getShape().keypointClass }),
    );
    this.connections$ = this.buildConnections$();
  }

  protected override buildAllSubpointInfo$() {
    return combineLatest([super.buildAllSubpointInfo$(), this.isFocused$]).pipe(
      map(([allBaseSubpointInfo, isFocused]) => {
        const useColors = this.isInOverlay || isFocused;
        return allBaseSubpointInfo.map((baseSubpointInfo, i) => {
          const keypointOption: KeypointOptionPoint | undefined = get(
            this.optionClass,
            ['points', i],
          );
          const color = keypointOption && keypointOption.color;
          return {
            ...baseSubpointInfo,
            renderAsDiamond: keypointOption && keypointOption.anchor,
            overrideColor: useColors && color ? color : undefined,
          };
        });
      }),
    );
  }

  protected override observeSubpointDeletability() {
    this.subscribeTo(this.store('editMode').$, (editMode) => {
      const cursor = editMode === 'remove' ? forbiddenCursor : null;
      setCssProperty(this.elementRef, '--h3-subpoint-cursor', cursor);
    });
  }

  private buildConnections$(): Observable<Connection[]> {
    if (!this.optionClass) {
      // if the class is invalid, don't render any connections
      return of([]);
    }

    return this.allSubpointInfo$.pipe(
      map((subpointInfo) =>
        this.optionClass!.connections.map((connectionOption) =>
          this.buildConnection(connectionOption, subpointInfo),
        ).filter(isDefined),
      ),
    );
  }

  private buildConnection(
    connectionOptions: KeypointOptionConnection,
    subpointInfo: SubpointInfo[],
  ) {
    const fromI = this.getPointIndex(connectionOptions.from);
    const toI = this.getPointIndex(connectionOptions.to);
    if (subpointInfo.length <= Math.max(fromI, toI)) {
      return undefined;
    }

    const from = subpointInfo[fromI];
    const to = subpointInfo[toI];
    return {
      pointString: getPointString([from.point, to.point]),
      color: from.overrideColor,
    };
  }

  private getPointIndex(name: string) {
    if (!this.optionClass) {
      throw new Error('Only call this when the keypoint class is valid');
    }
    return this.optionClass.points.findIndex(matches({ name }));
  }

  private getShape() {
    // We know it must be a kepoint shape if this component is rendering it
    return this.shapeStore.state() as KeypointShapeState;
  }
}
